import React, { useState } from 'react';
import { Alert, Button, Space, Tooltip, Badge, Typography, Divider } from 'antd';
import { UserSwitchOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import userImpersonationService from '../../../services/userImpersonationService';
import usePermission from '../../../hooks/usePermission';

const { Text } = Typography;

/**
 * Banner that displays when an admin is impersonating another user
 * It provides information about the impersonated user and allows ending impersonation
 * 
 * @param {Object} props Component props
 * @param {Function} props.onStopImpersonation Callback when impersonation is stopped
 */
const ImpersonationBanner = ({ onStopImpersonation }) => {
  const [stopping, setStopping] = useState(false);
  const { isImpersonating, impersonatedUser, adminUser } = usePermission();
  
  if (!isImpersonating) {
    return null;
  }
  
  const handleStopImpersonation = () => {
    setStopping(true);
    try {
      const result = userImpersonationService.stopImpersonation();
      
      if (result.success) {
        if (onStopImpersonation) {
          onStopImpersonation();
        }
        
        // Redirect to admin dashboard or saved return path with a small delay
        // to ensure everything is properly restored
        setTimeout(() => {
          window.location.href = result.returnPath || '/admin/dashboard';
        }, 500);
      } else {
        // Force reload in case of error
        setStopping(false);
       // window.location.reload();
      }
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      setStopping(false);
    //  window.location.reload();
    }
  };
  
  return (
    <Alert
      type="warning"
      banner
      message={
        <div className="flex justify-between items-center w-full">
          <Space size="middle">
            <Badge status="processing" color="#FFA500" />
            <div className="flex items-center">
              <UserSwitchOutlined className="mr-2" />
              <span>
                <Text strong>Admin Impersonation Mode:</Text> You are viewing the system as{' '}
                <Tooltip 
                  title={
                    <div>
                      <div>User ID: {impersonatedUser?.id}</div>
                      <div>Email: {impersonatedUser?.email}</div>
                      <Divider className="my-1" />
                      <div className="text-xs">You retain all your admin permissions</div>
                    </div>
                  }
                >
                  <Text strong className="underline cursor-help">
                    {impersonatedUser?.name || impersonatedUser?.email || 'User'}
                    <InfoCircleOutlined className="ml-1 text-xs" />
                  </Text>
                </Tooltip>
              </span>
            </div>
          </Space>
          
          <Space>
            <Text className="text-sm opacity-75">
              Logged in as admin: {adminUser?.name || adminUser?.email || 'Admin'}
            </Text>
            <Button 
              type="primary" 
              danger 
              size="small" 
              icon={<CloseCircleOutlined />}
              onClick={handleStopImpersonation}
              loading={stopping}
            >
              Stop Impersonation
            </Button>
          </Space>
        </div>
      }
      className="sticky top-0 z-50 right-0 m-0"
    />
  );
};

export default ImpersonationBanner;